/**
 * The drawer the transactional screens write through — orders, trips, invoices,
 * receipts, price boards, farmer registration.
 *
 * ResourcePage has its own drawer for declared master-data resources, where the
 * shape comes from a table declaration. These forms are not table rows: an order is
 * a header plus lines, a receipt is an amount split across invoices. So this one
 * takes the fields it is given and leaves the body free for whatever the form needs
 * underneath them.
 *
 * Same rule as everywhere else: never pre-judge a write. Send it, and show whatever
 * the server says back, because the server is where the rule lives.
 */
import { useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../lib/api'

export interface FormFieldDef {
  name: string
  label: string
  type?: 'text' | 'textarea' | 'number' | 'decimal' | 'money' | 'date' | 'datetime' | 'select' | 'bool'
  required?: boolean
  hint?: string
  options?: Array<{ value: string; label: string }>
  step?: number
  suffix?: string
  placeholder?: string
  /** Render this field on the same row as the one before it. */
  half?: boolean
}

export type FormValues = Record<string, unknown>

export function FormField({ def, value, onChange }: {
  def: FormFieldDef
  value: unknown
  onChange: (v: unknown) => void
}) {
  const id = `ff-${def.name}`

  if (def.type === 'bool') {
    return (
      <label className="check-row" htmlFor={id}>
        <input id={id} type="checkbox" checked={!!value}
          onChange={(e) => onChange(e.target.checked)} />
        <span><b>{def.label}</b>{def.hint && <i>{def.hint}</i>}</span>
      </label>
    )
  }

  const numeric = ['number', 'decimal', 'money'].includes(def.type ?? '')

  return (
    <div className="field">
      <label htmlFor={id}>
        {def.label}
        {def.required && <span className="crit-t"> *</span>}
      </label>

      {def.type === 'textarea' ? (
        <textarea id={id} className="input" rows={2} value={String(value ?? '')}
          placeholder={def.placeholder} onChange={(e) => onChange(e.target.value)}
          required={def.required} />
      ) : def.type === 'select' ? (
        <select id={id} className="input" value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)} required={def.required}>
          <option value="">— choose —</option>
          {(def.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className={`input ${numeric ? 'n' : ''}`}
          type={def.type === 'date' ? 'date'
            : def.type === 'datetime' ? 'datetime-local'
            : numeric ? 'number' : 'text'}
          step={def.step ?? (def.type === 'decimal' || def.type === 'money' ? 0.01 : undefined)}
          value={String(value ?? '')}
          placeholder={def.placeholder}
          onChange={(e) => onChange(e.target.value)}
          required={def.required}
        />
      )}

      {def.hint && <i className="hint">{def.hint}</i>}
    </div>
  )
}

export function FormDrawer({
  title, kind, fields, values, onChange, onSubmit, onClose,
  submitLabel, children, footer, wide,
}: {
  title: string
  /** The small line above the title — "New", "Allocate", "Record". */
  kind?: string
  fields?: FormFieldDef[]
  values: FormValues
  onChange: (name: string, v: unknown) => void
  onSubmit: () => Promise<void>
  onClose: () => void
  submitLabel: string
  /** Anything the form needs below the declared fields: line editors, pickers. */
  children?: ReactNode
  /** A running total or a warning, shown above the buttons. */
  footer?: ReactNode
  wide?: boolean
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await onSubmit()
    } catch (err) {
      setError(err instanceof ApiError ? err.message
        : err instanceof Error ? err.message
        : 'That did not work.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="drawer-scrim"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <form className={`drawer ${wide ? 'wide' : ''}`} onSubmit={submit}>
        <header className="drawer-head">
          <div>
            {kind && <span className="micro">{kind}</span>}
            <h3>{title}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="drawer-body">
          {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}

          {fields && fields.length > 0 && (
            <div className="field-pairs">
              {fields.map((f) => (
                <div key={f.name} className={f.half ? 'half' : ''}>
                  <FormField def={f} value={values[f.name]} onChange={(v) => onChange(f.name, v)} />
                </div>
              ))}
            </div>
          )}

          {children}
        </div>

        <footer className="drawer-foot">
          {footer && <div className="drawer-total">{footer}</div>}
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>{busy ? 'Working…' : submitLabel}</button>
        </footer>
      </form>
    </div>
  )
}

/** Confirm-then-act, for the one-click verbs that still deserve a pause. */
export function useConfirmAction() {
  const [busy, setBusy] = useState(false)

  async function run(question: string, act: () => Promise<unknown>, after?: () => void) {
    if (!window.confirm(question)) return false
    setBusy(true)
    try {
      await act()
      after?.()
      return true
    } finally {
      setBusy(false)
    }
  }

  return { busy, run }
}
